import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Phone,
} from "lucide-react";
import { getPostBySlug, getRelatedPosts } from "@/data/blogPosts";
import { BRAND } from "@/lib/constants";

function formatDate(iso) {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function ContentBlocks({ blocks }) {
  return (
    <>
      {blocks.map((b, idx) => {
        if (b.type === "p") {
          return (
            <p
              key={idx}
              className="mt-5 text-[17px] leading-[1.75] text-[#CBD5E1]"
            >
              {b.text}
            </p>
          );
        }
        if (b.type === "h2") {
          return (
            <h2
              key={idx}
              className="mt-12 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white"
            >
              {b.text}
            </h2>
          );
        }
        if (b.type === "h3") {
          return (
            <h3
              key={idx}
              className="mt-8 font-display text-xl font-semibold tracking-tight text-white"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul
              key={idx}
              className="mt-5 space-y-2.5 text-[16px] leading-[1.7] text-[#CBD5E1]"
            >
              {b.items.map((it, i) => (
                <li key={i} className="pl-6 relative">
                  <span className="absolute left-0 top-[10px] h-1.5 w-1.5 rounded-full bg-[#28C0D8]" />
                  {it}
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol
              key={idx}
              className="mt-5 space-y-2.5 text-[16px] leading-[1.7] text-[#CBD5E1] list-decimal pl-6 marker:text-[#28C0D8]"
            >
              {b.items.map((it, i) => (
                <li key={i} className="pl-2">
                  {it}
                </li>
              ))}
            </ol>
          );
        }
        if (b.type === "callout") {
          return (
            <div
              key={idx}
              className="mt-8 rounded-2xl border border-[#28C0D8]/25 bg-[#28C0D8]/[0.06] p-5 md:p-6"
            >
              <p className="text-[15px] leading-[1.7] text-white/90">
                {b.text}
              </p>
            </div>
          );
        }
        if (b.type === "faq") {
          return (
            <div key={idx} className="mt-8 space-y-4">
              {b.items.map((f, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6"
                  data-testid={`blog-faq-item-${i}`}
                >
                  <p className="font-display text-[17px] font-semibold text-white">
                    {f.q}
                  </p>
                  <p className="mt-2 text-[15px] leading-[1.7] text-[#94A3B8]">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const related = useMemo(() => getRelatedPosts(slug, 3), [slug]);

  useEffect(() => {
    if (!post) return;
    const url = `https://mdsupplywellness.com/blog/${post.slug}`;
    document.title = `${post.title} | MD Supply Wellness`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", post.excerpt);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Article JSON-LD
    const article = document.createElement("script");
    article.type = "application/ld+json";
    article.id = "blog-post-jsonld";
    article.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.image ? [post.image] : undefined,
      keywords: (post.keywords || []).join(", "),
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Organization",
        name: "MD Supply Wellness Team",
        url: "https://mdsupplywellness.com/about",
      },
      publisher: {
        "@type": "Organization",
        name: "MD Supply Wellness",
        url: "https://mdsupplywellness.com",
        logo: {
          "@type": "ImageObject",
          url: "https://mdsupplywellness.com/logo.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url,
      },
      articleSection: (post.tags && post.tags[0]) || "Cannabis Education",
      inLanguage: "en-US",
    });
    document.head.appendChild(article);

    // FAQPage JSON-LD (AEO)
    const faqBlock = post.content.find((b) => b.type === "faq");
    let faqNode = null;
    if (faqBlock && faqBlock.items && faqBlock.items.length) {
      faqNode = document.createElement("script");
      faqNode.type = "application/ld+json";
      faqNode.id = "blog-post-faq-jsonld";
      faqNode.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqBlock.items.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
          },
        })),
      });
      document.head.appendChild(faqNode);
    }

    return () => {
      const a = document.getElementById("blog-post-jsonld");
      if (a) a.remove();
      const f = document.getElementById("blog-post-faq-jsonld");
      if (f) f.remove();
      document.title = "MD Supply Wellness — Bethesda, Maryland";
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div data-testid="blog-post-page">
      {/* HERO */}
      <section className="py-12 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          {/* Breadcrumbs */}
          <nav
            aria-label="Breadcrumb"
            className="mb-8 flex items-center gap-1 text-xs text-white/50"
            data-testid="blog-breadcrumbs"
          >
            <Link to="/" className="hover:text-[#28C0D8]">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <Link to="/education" className="hover:text-[#28C0D8]">
              Education
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <Link to="/blog" className="hover:text-[#28C0D8]">
              Blog
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <span className="text-white/80 line-clamp-1">{post.title}</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 text-xs text-white/50">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(post.date)}
            </span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {post.readingTime} read
            </span>
            {post.tags && post.tags[0] && (
              <>
                <span className="w-1 h-1 rounded-full bg-white/30" />
                <span className="uppercase tracking-wider text-[11px] text-[#28C0D8]">
                  {post.tags[0]}
                </span>
              </>
            )}
          </div>

          <h1 className="mt-4 font-display text-[30px] leading-[1.1] sm:text-4xl lg:text-[52px] font-semibold tracking-tight text-white">
            {post.title}
          </h1>
          <p className="mt-6 text-[18px] leading-[1.6] text-[#94A3B8]">
            {post.excerpt}
          </p>
        </div>

        {/* Cover */}
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-10">
          <div className="relative aspect-[16/8] rounded-3xl overflow-hidden border border-white/10">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/70 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-16 md:pb-24">
        <article className="max-w-[780px] mx-auto px-6 md:px-10">
          <ContentBlocks blocks={post.content} />

          {/* Author card */}
          <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="h-14 w-14 rounded-full bg-[#28C0D8]/15 border border-[#28C0D8]/30 flex items-center justify-center shrink-0">
              <span className="font-display text-lg text-[#28C0D8]">MD</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] text-white/50">
                Written by
              </p>
              <p className="mt-1 font-display text-white text-lg font-semibold">
                MD Supply Wellness Team
              </p>
              <p className="mt-1 text-sm text-[#94A3B8] inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {BRAND.address.line1},{" "}
                {BRAND.address.line2}
              </p>
            </div>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-medium text-white hover:bg-white/5"
              data-testid="blog-post-call-btn"
            >
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
          </div>

          {/* Back to blog */}
          <div className="mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-[#28C0D8] hover:text-white"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
          </div>
        </article>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                  Keep learning
                </p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white">
                  Related articles
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#28C0D8] hover:text-white"
              >
                All articles <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#28C0D8]/40 transition-all overflow-hidden min-w-0"
                  data-testid={`blog-related-${p.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1 min-w-0">
                    <span className="text-[11px] uppercase tracking-wider text-white/50">
                      {formatDate(p.date)}
                    </span>
                    <h3 className="mt-2 font-display text-lg font-semibold tracking-tight text-white group-hover:text-[#28C0D8] transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
